import { useRef, useState } from "react";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { api, digits, errMsg, maskCep, maskDoc, maskPhone } from "@/lib/api";
import { validDoc } from "@/lib/document";
import { cn } from "@/lib/utils";
import { CnhUpload } from "./CnhUpload";

const NO_ADDR = { cep: "", logradouro: "", numero: "", complemento: "", bairro: "", cidade: "", uf: "" };
const TONE = { error: "text-red-500", ok: "text-emerald-500", checking: "text-muted-foreground" };

export const formOf = (c, responsibleId) => {
  const [first = "", ...rest] = (c?.name || "").split(" ");
  return {
    first_name: c?.first_name ?? first, last_name: c?.last_name ?? rest.join(" "), document: maskDoc(c?.document || ""),
    phone: maskPhone(c?.phone || ""), whatsapp: maskPhone(c?.whatsapp || ""), company: c?.company || "",
    address: { ...NO_ADDR, ...(c?.address || {}), cep: maskCep(c?.address?.cep || "") }, plate: c?.plate || "", cnh_number: c?.cnh_number || "",
    cnh_file: c?.cnh_file || null, responsible_id: c?.responsible_id ?? responsibleId ?? null, active: c?.active ?? true,
  };
};

const missing = (f) => (!f.first_name.trim() ? "Informe o nome" : !f.last_name.trim() ? "Informe o sobrenome" : !validDoc(digits(f.document)) ? "CPF ou CNPJ inválido"
  : digits(f.phone).length < 10 ? "Informe o telefone com DDD" : digits(f.whatsapp).length < 10 ? "Informe o WhatsApp com DDD" : "");

function useDocLookup(setF) {
  const [st, setSt] = useState({});
  const last = useRef("");
  const check = async (raw) => {
    const d = digits(raw);
    if (d === last.current) return;
    last.current = d;
    if (!d) return setSt({});
    if (!validDoc(d)) return setSt({ status: "error", msg: d.length === 14 ? "CNPJ inválido" : d.length === 11 ? "CPF inválido" : "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)" });
    if (d.length === 11) return setSt({ status: "ok", msg: "CPF válido" });
    setSt({ status: "checking", msg: "Buscando dados da empresa na Receita…" });
    try {
      const { data } = await api.get(`/auth/cnpj/${d}`);
      const a = data.endereco;
      setF((x) => ({ ...x, company: data.razao_social || x.company, address: { ...x.address, ...Object.fromEntries(Object.entries({ ...a, cep: maskCep(a.cep) }).filter(([, v]) => v)) } }));
      setSt({ status: "ok", msg: `CNPJ válido · ${data.razao_social || "empresa"} · endereço preenchido` });
    } catch {
      setSt({ status: "ok", msg: "CNPJ válido · não achamos os dados na Receita, preencha o endereço" });
    }
  };
  return [st, check];
}

function AddressFields({ a, setA }) {
  const lookupCep = async () => {
    const cep = digits(a.cep);
    if (cep.length !== 8) return;
    try {
      const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`).then((x) => x.json());
      if (!r.erro) setA({ logradouro: r.logradouro || a.logradouro, bairro: r.bairro || a.bairro, cidade: r.localidade || a.cidade, uf: r.uf || a.uf });
    } catch {
      /* preenchimento manual */
    }
  };
  const inp = (k, testId, props = {}) => <Input value={a[k]} onChange={(e) => setA({ [k]: props.mask ? props.mask(e.target.value) : e.target.value })} data-testid={testId} {...(props.extra || {})} />;
  return (
    <div className="grid gap-3 sm:grid-cols-6">
      <Field label="CEP" className="sm:col-span-2">{inp("cep", "courier-cep-input", { mask: maskCep, extra: { onBlur: lookupCep, inputMode: "numeric" } })}</Field>
      <Field label="Logradouro" className="sm:col-span-4">{inp("logradouro", "courier-street-input")}</Field>
      <Field label="Número" className="sm:col-span-2">{inp("numero", "courier-number-input")}</Field>
      <Field label="Complemento" className="sm:col-span-4">{inp("complemento", "courier-complement-input")}</Field>
      <Field label="Bairro" className="sm:col-span-2">{inp("bairro", "courier-district-input")}</Field>
      <Field label="Cidade" className="sm:col-span-3">{inp("cidade", "courier-city-input")}</Field>
      <Field label="UF" className="sm:col-span-1">{inp("uf", "courier-uf-input", { mask: (v) => v.toUpperCase().slice(0, 2) })}</Field>
    </div>
  );
}

const Section = ({ title, children }) => <section className="space-y-3"><p className="eyebrow">{title}</p>{children}</section>;

export function CourierForm({ courier, responsible, onBack, onSaved }) {
  const [f, setF] = useState(() => formOf(courier, responsible?.id));
  const [busy, setBusy] = useState(false);
  const [docSt, checkDoc] = useDocLookup(setF);
  const set = (k, mask) => (e) => setF((x) => ({ ...x, [k]: mask ? mask(e.target.value) : e.target.value }));
  const onDoc = (e) => {
    const v = maskDoc(e.target.value);
    setF((x) => ({ ...x, document: v }));
    if (digits(v).length === 14) checkDoc(v);
  };
  const save = async () => {
    const err = missing(f);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const body = { ...f, document: digits(f.document), phone: digits(f.phone), whatsapp: digits(f.whatsapp), address: { ...f.address, cep: digits(f.address.cep) } };
      if (courier) await api.put(`/couriers/${courier.id}`, body);
      else await api.post("/couriers", body);
      toast.success(courier ? "Entregador atualizado" : "Entregador cadastrado");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6" data-testid="courier-form">
      <button type="button" onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="courier-form-back"><ArrowLeft className="h-4 w-4" />Voltar para a lista</button>
      {responsible && <p className="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary" data-testid="courier-form-responsible">Entregador vinculado a {responsible.name} (responsável)</p>}
      <Section title="Dados do entregador">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nome *"><Input value={f.first_name} onChange={set("first_name")} data-testid="courier-first-name-input" /></Field>
          <Field label="Sobrenome *"><Input value={f.last_name} onChange={set("last_name")} data-testid="courier-last-name-input" /></Field>
          <Field label="CPF ou CNPJ *" hint={docSt.msg && <span className={TONE[docSt.status]} data-testid="courier-document-status">{docSt.msg}</span>}>
            <Input value={f.document} onChange={onDoc} onBlur={() => checkDoc(f.document)} inputMode="numeric" className={cn(docSt.status === "error" && "border-red-500")} data-testid="courier-document-input" />
          </Field>
          <Field label="Empresa" hint="Preenchida pelo CNPJ"><Input value={f.company} onChange={set("company")} data-testid="courier-company-input" /></Field>
          <Field label="Telefone *"><Input value={f.phone} onChange={set("phone", maskPhone)} inputMode="tel" data-testid="courier-phone-input" /></Field>
          <Field label="WhatsApp com DDD *" hint="Recebe os pedidos do módulo Motoboy"><Input value={f.whatsapp} onChange={set("whatsapp", maskPhone)} inputMode="tel" data-testid="courier-whatsapp-input" /></Field>
        </div>
      </Section>
      <Section title="Endereço (opcional)"><AddressFields a={f.address} setA={(v) => setF((x) => ({ ...x, address: { ...x.address, ...v } }))} /></Section>
      <Section title="Veículo e CNH (opcional)">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Placa do veículo"><Input value={f.plate} onChange={set("plate", (v) => v.toUpperCase().slice(0, 8))} placeholder="ABC1D23" data-testid="courier-plate-input" /></Field>
          <Field label="Número da CNH"><Input value={f.cnh_number} onChange={set("cnh_number", (v) => digits(v).slice(0, 11))} inputMode="numeric" data-testid="courier-cnh-number-input" /></Field>
        </div>
        <CnhUpload value={f.cnh_file} courierId={courier?.id} savedPath={courier?.cnh_file?.path} onChange={(v) => setF((x) => ({ ...x, cnh_file: v }))} />
      </Section>
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={onBack} data-testid="courier-form-cancel">Cancelar</Button>
        <Button onClick={save} disabled={busy} data-testid="courier-form-save">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salvar</Button>
      </div>
    </div>
  );
}
